import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { closeStaffLive, setStaffSignedIn } from './live'
import { api } from './api'

/**
 * Three tiers, matching the backend exactly (see CLAUDE.md):
 *
 *   admin        superadmin — config editing
 *   data_admin   the CEO and the practice manager both ride on this role, and are
 *                separated only by `accountRole`
 *   practitioner their own invoices
 *   platform_admin  whoever runs Lynqpod, above every practice. Deliberately NOT a
 *                   variant of admin: a practice's own superadmin must never be able
 *                   to reach another practice.
 */
export type Role = 'admin' | 'data_admin' | 'practitioner' | 'platform_admin'
/** 'staff': a teammate's login from Team, limited to `access` (server: utils/staffAccess.js). */
export type AccountRole = 'ceo' | 'practice_manager' | 'staff' | null

export interface User {
  id: string
  username: string
  role: Role
  accountRole: AccountRole
  practitionerId?: number
  /** Present on practitioner tokens only, and stored lower-cased. */
  practitionerName?: string
  /** Signed in with a temporary password: must choose their own first. */
  mustChangePassword?: boolean
  /** A teammate login's modules, and their name. */
  access?: string[]
  displayName?: string
}

interface AuthValue {
  user: User | null
  loading: boolean
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => void
  /** Reads /auth/me again (after a password change). */
  refresh: () => Promise<void>
  /** practice managers may not change commission rates */
  canEditCommission: boolean
}

const Ctx = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Signed in or not is the server's answer: the cookie cannot be looked at from here.
  useEffect(() => {
    api.get('/auth/me')
      .then((r) => { setUser(r.data.user as User); setStaffSignedIn(true) })
      .catch(() => { setUser(null); setStaffSignedIn(false) })
      .finally(() => setLoading(false))
  }, [])

  const signIn = async (username: string, password: string) => {
    await api.post('/sign-in', { username, password })
    const me = await api.get('/auth/me')
    setStaffSignedIn(true)
    setUser(me.data.user as User)
  }

  const refresh = async () => {
    const me = await api.get('/auth/me')
    setUser(me.data.user as User)
  }

  const signOut = () => {
    closeStaffLive()
    setStaffSignedIn(false)
    setUser(null)
    void api.post('/auth/sign-out').catch(() => { /* the cookie expires by itself */ })
  }

  const canEditCommission = user?.role === 'admin'
    || (user?.role === 'data_admin' && user.accountRole !== 'practice_manager')

  return (
    <Ctx.Provider value={{ user, loading, signIn, signOut, refresh, canEditCommission }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>')
  return v
}

/**
 * Who to show in the sidebar. A practitioner's username is their Dentally id, which
 * is meaningless to them, so prefer the name the login carries — stored lower-cased,
 * so it is title-cased for display only.
 */
export function displayName(u: User | null): string {
  if (!u) return ''
  if (u.accountRole === 'staff' && u.displayName) return u.displayName
  if (u.role !== 'practitioner' || !u.practitionerName) return u.username
  return u.practitionerName.replace(/\b[a-z]/g, (c) => c.toUpperCase())
}

/** A readable label for the tier, since `data_admin` means two different jobs. */
export function roleLabel(u: User | null): string {
  if (!u) return ''
  if (u.role === 'admin') return 'Superadmin'
  if (u.role === 'practitioner') return 'Practitioner'
  if (u.accountRole === 'practice_manager') return 'Practice manager'
  if (u.accountRole === 'ceo') return 'CEO'
  if (u.accountRole === 'staff') return 'Team member'
  return 'Admin'
}
